import React, { useState, useEffect } from "react";
import api from "../services/api";
import "./AnniversaireForm.css";

interface ThemeData {
  background: string;
  text: string;
  accent: string;
  button: string;
}

interface FormDataState {
  nom: string;
  age: string;
  province: string;
  dateISO: string;
  heureISO: string;
  lieu: string;
  image: File | null;
  imageUrl: string;
  rsvp: boolean;
  theme: ThemeData;
}

interface ProvincesResponse {
  success: boolean;
  provinces: string[];
}

interface AnniversaireResponse {
  success: boolean;
  id: string;
  anniversaire?: {
    _id: string;
    personne: string;
    age: number;
  };
  message?: string;
}

interface InviteResponse {
  success: boolean;
  invite: {
    _id: string;
    nom: string;
    uniqueToken: string;
  };
  lienUnique: string;
}

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;

const AnniversaireForm: React.FC = () => {
  const [provinces, setProvinces] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormDataState>({
    nom: "",
    age: "",
    province: "",
    dateISO: "",
    heureISO: "",
    lieu: "",
    image: null,
    imageUrl: "",
    rsvp: false,
    theme: {
      background: "#ffffff",
      text: "#000000",
      accent: "#ffb347",
      button: "#ffb347"
    }
  });

  useEffect(() => {
    fetchProvinces();
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await api.get<ProvincesResponse>("/admin/users/provinces");
      if (response.data.success) {
        setProvinces(response.data.provinces);
      }
    } catch (error) {
      console.error("Erreur chargement provinces:", error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      theme: { ...prev.theme, [name]: value }
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const fileUrl = URL.createObjectURL(file);
      setFormData(prev => ({ 
        ...prev, 
        image: file,
        imageUrl: fileUrl
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nom.trim()) {
      alert("Veuillez entrer le nom de la personne");
      return;
    }

    if (!formData.age.trim()) {
      alert("Veuillez entrer l'âge");
      return;
    }

    if (!formData.image) {
      alert("Veuillez sélectionner une image pour l'anniversaire");
      return;
    }

    if (!formData.dateISO || !formData.heureISO || !formData.province) {
      alert("Veuillez remplir tous les champs obligatoires : date, heure et province");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('personne', formData.nom);
    formDataToSend.append('age', formData.age);
    formDataToSend.append('lieu', formData.lieu);
    formDataToSend.append('province', formData.province);
    formDataToSend.append('dateISO', formData.dateISO);
    formDataToSend.append('heureISO', formData.heureISO);
    formDataToSend.append('rsvp', formData.rsvp.toString());
    formDataToSend.append('theme', JSON.stringify(formData.theme));
    formDataToSend.append('image', formData.image);

    try {
      const response = await api.post<AnniversaireResponse>("/anniversaires", formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        const anniversaireId = response.data.id;
        
        const inviteResponse = await api.post<InviteResponse>(`/anniversaires/${anniversaireId}/invites`, {
          nom: formData.nom,
          telephone: ""
        });
        
        if (inviteResponse.data.success) {
          const token = inviteResponse.data.invite.uniqueToken;
          const lienAnniversaire = `${FRONTEND_URL}/anniversaire/${token}`;
          
          alert(`Anniversaire créé avec succès !\n\nLien d'invitation : ${lienAnniversaire}\n\nPartagez ce lien avec les invités.`);
        } else {
          alert("Anniversaire créé mais erreur lors de la création de l'invitation");
        }
      } else {
        alert("Erreur lors de la création de l'anniversaire");
      }

      if (formData.imageUrl) {
        URL.revokeObjectURL(formData.imageUrl);
      }

      setFormData({
        nom: "",
        age: "",
        province: "",
        dateISO: "",
        heureISO: "",
        lieu: "",
        image: null,
        imageUrl: "",
        rsvp: false,
        theme: {
          background: "#ffffff",
          text: "#000000",
          accent: "#ffb347",
          button: "#ffb347"
        }
      });

    } catch (err: any) {
      console.error("Erreur complète:", err);
      const errorMessage = err.response?.data?.message || err.message || "Erreur inconnue";
      alert(`Erreur : ${errorMessage}`);
    }
  };

  return (
    <div className="anniversaire-form-container">
      <h2>Créer un anniversaire</h2>
      <form onSubmit={handleSubmit} className="anniversaire-form">
        
        <div className="form-section">
          <h3>Informations de la personne</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Nom complet *</label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                placeholder="Ex: Jean Mbala"
                required
              />
            </div>

            <div className="form-group">
              <label>Âge *</label>
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                placeholder="Ex: 60"
                min="0"
                max="120"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Lieu de la fête</label>
            <input
              type="text"
              name="lieu"
              value={formData.lieu}
              onChange={handleChange}
              placeholder="Ex: Cercle Elais, Kinshasa"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Informations générales</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                name="dateISO"
                value={formData.dateISO}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Heure *</label>
              <input
                type="time"
                name="heureISO"
                value={formData.heureISO}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Province *</label>
            <select name="province" value={formData.province} onChange={handleChange} required>
              <option value="">Sélectionnez une province</option>
              {provinces.map(prov => (
                <option key={prov} value={prov}>{prov}</option>
              ))}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="rsvp"
                checked={formData.rsvp}
                onChange={handleChange}
              />
              Activer la confirmation de présence (RSVP)
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3>Image de l'anniversaire</h3>
          <p className="info-message">
            L'image peut contenir des informations supplémentaires (message, décorations, etc.)
          </p>
          
          <div className="form-group">
            <label>Image *</label>
            <input 
              type="file" 
              name="image" 
              accept="image/*" 
              onChange={handleFileChange}
              required
            />
            <small className="help-text">
              Sélectionnez une image pour illustrer votre anniversaire.
            </small>
            {formData.imageUrl && (
              <div className="image-preview">
                <img src={formData.imageUrl} alt="Aperçu" />
                <small>{formData.image?.name}</small>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Personnalisation des couleurs</h3>
          <p className="info-message">
            Choisissez les couleurs pour harmoniser l'affichage avec votre image.
          </p>
          
          <div className="form-row">
            <div className="form-group">
              <label>Couleur de fond</label>
              <input
                type="color"
                name="background"
                value={formData.theme.background}
                onChange={handleThemeChange}
              />
              <small>Fond de la page</small>
            </div>

            <div className="form-group">
              <label>Couleur du texte</label>
              <input
                type="color"
                name="text"
                value={formData.theme.text}
                onChange={handleThemeChange}
              />
              <small>Couleur des textes</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Couleur d'accent</label>
              <input
                type="color"
                name="accent"
                value={formData.theme.accent}
                onChange={handleThemeChange}
              />
              <small>Bordures, séparateurs</small>
            </div>

            <div className="form-group">
              <label>Couleur des boutons</label>
              <input
                type="color"
                name="button"
                value={formData.theme.button}
                onChange={handleThemeChange}
              />
              <small>Boutons d'action</small>
            </div>
          </div>

          <div className="theme-preview">
            <p>Aperçu :</p>
            <div 
              className="preview-box" 
              style={{ 
                backgroundColor: formData.theme.background,
                color: formData.theme.text,
                border: `1px solid ${formData.theme.accent}`
              }}
            >
              <span style={{ color: formData.theme.text }}>Texte exemple</span>
              <button 
                style={{ 
                  backgroundColor: formData.theme.button,
                  color: formData.theme.background
                }}
              >
                Bouton
              </button>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn">
            Créer l'anniversaire
          </button>
          <button 
            type="button" 
            className="reset-btn"
            onClick={() => {
              if (window.confirm("Voulez-vous réinitialiser le formulaire ?")) {
                if (formData.imageUrl) {
                  URL.revokeObjectURL(formData.imageUrl);
                }
                
                setFormData({
                  nom: "",
                  age: "",
                  province: "",
                  dateISO: "",
                  heureISO: "",
                  lieu: "",
                  image: null,
                  imageUrl: "",
                  rsvp: false,
                  theme: {
                    background: "#ffffff",
                    text: "#000000",
                    accent: "#ffb347",
                    button: "#ffb347"
                  }
                });
              }
            }}
          >
            Réinitialiser
          </button>
        </div>
      </form>
    </div>
  );
};

export default AnniversaireForm;